import React, { useCallback, useMemo } from "react";
import TextTitle from "../common/ui/typography/TextTitle";
import Button from "../common/ui/buttons/Button";
import "./style.scss";
import TextCallout from "../common/ui/typography/TextCallout";
import TextInput from "../common/form/TextInput";
import Checkbox from "../common/form/Checkbox";
import AsyncSelect from "react-select/async";
import { debounce } from "lodash";
import { statistics } from "../../api/statistics";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useGlobalStore } from "../../store";
import { useMutation } from "react-query";
import { useAlert } from "../../hooks/useAlert";

type FormValues = {
  android: boolean;
  androidVersion: number;
  ios: boolean;
  iosVersion: number;
  companies: { label: string; value: number }[];
  drivers: { label: string; value: number }[];
};

const themedSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: 8,
    borderColor: state.isFocused ? "#ffb94a" : "var(--theme-color-grey-2)",
    boxShadow: state.isFocused ? "0 0 0 1px #ffb94a" : "none",
    backgroundColor: "var(--theme-input-bg)",
    color: "var(--theme-color-primary-text)",
    "&:hover": {
      borderColor: "#ffb94a",
    },
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid var(--theme-color-grey-2)",
    backgroundColor: "var(--theme-color-bg-elevated)",
    boxShadow: "var(--theme-box-shadow-dropdown)",
    overflow: "hidden",
  }),
  menuList: (base: any) => ({
    ...base,
    backgroundColor: "var(--theme-color-bg-elevated)",
    color: "var(--theme-color-primary-text)",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "var(--theme-color-primary-text)",
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: "var(--theme-color-grey-1)",
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: "var(--theme-color-primary-text)",
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: "var(--theme-color-grey-3)",
    ":hover": {
      backgroundColor: "var(--theme-color-grey-2)",
      color: "var(--theme-color-primary-text)",
    },
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "var(--theme-color-grey-3)",
  }),
  input: (base: any) => ({
    ...base,
    color: "var(--theme-color-primary-text)",
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    backgroundColor: "var(--theme-color-grey-2)",
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "var(--theme-color-grey-3)",
    ":hover": {
      color: "var(--theme-color-primary-text)",
    },
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#ffb94a"
      : state.isFocused
        ? "var(--theme-color-grey-1)"
        : "transparent",
    color: state.isSelected ? "#fff" : "var(--theme-color-primary-text)",
  }),
};

export const ForceUpdateCreate = () => {
  const selectedCompany = useGlobalStore(state => state.selectedCompany);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { register, handleSubmit, control, watch } = useForm<FormValues>({
    defaultValues: {
      android: false,
      androidVersion: 0,
      ios: false,
      iosVersion: 0,
      companies: [],
      drivers: [],
    },
  });

  const androidChecked = watch("android");
  const iosChecked = watch("ios");
  const selectedCompanies = watch("companies");

  const mutation = useMutation({
    mutationFn: (data: FormValues) =>
      statistics.editUsersByEld(
        {
          companyIds: data.companies?.map(el => el.value),
          userIds: data.drivers?.map(el => el.value),
          isAllDrivers: !data.drivers?.length && !data.companies?.length,
          latestAndroidVersionCode: data.android
            ? +data.androidVersion
            : undefined,
          latestIOSVersionCode: data.ios ? +data.iosVersion : undefined,
        },
        selectedCompany,
      ),
    onSuccess: data => {
      showAlert(
        "success",
        data?.data?.data?.message || "Successfully updated!",
      );
      navigate("/force-update");
    },
    onError: (err: any) => {
      showAlert(
        "error",
        err?.response?.data?.error.message || "Something went wrong!",
      );
    },
  });

  const onSubmit = (data: FormValues) => {
    mutation.mutate(data);
  };

  const loadCompanyOptions = async (
    inputValue: string,
    callback: (options: { value: number; label: string }[]) => void,
  ) => {
    try {
      statistics.drivers({
        eldType: selectedCompany,
      });
      const { data } = await statistics.companiesList({
        searchName: inputValue,
        page: 1,
        perPage: 50,
        selectedCompany,
      });

      const options = data.map((company: { id: number; name: string }) => ({
        value: company.id,
        label: company.name,
      }));

      callback(options);
      return options;
    } catch (error) {
      console.error("Error loading companies:", error);
      callback([]);
      return [];
    }
  };

  const debouncedLoadOptions = useMemo(
    () =>
      debounce(
        (
          inputValue: string,
          callback: (options: { value: number; label: string }[]) => void,
        ) => {
          loadCompanyOptions(inputValue, callback);
        },
        500,
      ),
    [],
  );

  const loadDriverOptions = useCallback(
    async (
      inputValue: string,
      callback: (options: { value: number; label: string }[]) => void,
    ) => {
      const watchedCompanies = watch("companies");

      const companyIds = (watchedCompanies ?? []).map(c => c.value);

      try {
        const { data } = await statistics.drivers({
          searchName: inputValue,
          page: 1,
          perPage: 100,
          eldType: selectedCompany,
          companyIds,
        });

        const options = data.map(
          (driver: {
            id: number;
            first_name: string;
            second_name: string;
            companyId: number;
          }) => ({
            value: driver.id,
            label: driver.first_name + " " + driver.second_name,
            companyId: driver.companyId,
          }),
        );

        callback(options);
        return options;
      } catch (error) {
        console.error("Error loading drivers:", error);
        callback([]);
        return [];
      }
    },
    [watch, selectedCompany],
  );

  const debouncedLoadDriverOptions = useMemo(
    () =>
      debounce(
        (
          inputValue: string,
          callback: (options: { value: number; label: string }[]) => void,
        ) => {
          loadDriverOptions(inputValue, callback);
        },
        500,
      ),
    [],
  );

  const isValidForm = useMemo(() => {
    const androidFilled = androidChecked && !!watch("androidVersion");
    const iosFilled = iosChecked && !!watch("iosVersion");

    return androidFilled || iosFilled;
  }, [
    androidChecked,
    iosChecked,
    watch("androidVersion"),
    watch("iosVersion"),
  ]);

  return (
    <form className='update-create' onSubmit={handleSubmit(onSubmit)}>
      <div className='force-update__header d-flex align-items-center'>
        <TextTitle level='large' className='d-flex align-center gap-2'>
          New Force Update
        </TextTitle>
      </div>
      <div className='update-create__form'>
        <div className='d-flex flex-column gap-3'>
          <TextCallout level='2'>Select system</TextCallout>
          <div
            className='d-flex gap-3 w-100'
            style={{
              paddingBottom: "24px",
              borderBottom: "0.5px solid var(--theme-color-grey-2)",
            }}
          >
            <div className='d-flex flex-column gap-2 w-100'>
              <Controller
                name='android'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    modelValue={field.value}
                    onModelValueUpdate={val => field.onChange(val)}
                    label='Android'
                  />
                )}
              />
              <Controller
                name='androidVersion'
                control={control}
                render={({ field }) => (
                  <TextInput
                    placeholder='Version code'
                    {...field}
                    disabled={!androidChecked}
                    type='number'
                  />
                )}
              />
            </div>
            <div className='d-flex flex-column gap-2 w-100'>
              <Controller
                name='ios'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    modelValue={field.value}
                    onModelValueUpdate={val => field.onChange(val)}
                    label='IOS'
                  />
                )}
              />
              <Controller
                name='iosVersion'
                control={control}
                render={({ field }) => (
                  <TextInput
                    placeholder='Version code'
                    {...field}
                    disabled={!iosChecked}
                    type='number'
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className='d-flex flex-column gap-2 w-100'>
          <TextCallout level='2'>Companies</TextCallout>
          <Controller
            name='companies'
            control={control}
            render={({ field }) => (
              <AsyncSelect
                {...field}
                cacheOptions
                defaultOptions
                loadOptions={debouncedLoadOptions}
                isMulti
                placeholder='Select companies'
                styles={themedSelectStyles}
              />
            )}
          />
        </div>

        <div className='d-flex flex-column gap-2 w-100'>
          <TextCallout level='2'>Drivers</TextCallout>
          <Controller
            name='drivers'
            control={control}
            disabled={!selectedCompanies.length}
            render={({ field }) => (
              <AsyncSelect
                key={JSON.stringify(selectedCompanies)}
                {...field}
                cacheOptions
                defaultOptions
                loadOptions={debouncedLoadDriverOptions}
                isMulti
                placeholder='Select drivers'
                isDisabled={!selectedCompanies.length}
                styles={themedSelectStyles}
              />
            )}
          />
        </div>

        <div className='d-flex gap-3'>
          <Button
            className='w-100'
            size='sm'
            to='/force-update'
            onClick={() => navigate("/force-update")}
            esc
          >
            Cancel
          </Button>
          <Button
            className='w-100'
            size='sm'
            type='submit'
            enter
            main={isValidForm}
            disabled={!isValidForm || mutation.isLoading}
            loading={mutation.isLoading}
          >
            Create
          </Button>
        </div>
      </div>
    </form>
  );
};
